// Campaign manager component
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { adminAPI } from '../../services/api';
import Loading from '../common/Loading';
import toast from 'react-hot-toast';
import { 
  MegaphoneIcon,
  PhoneIcon,
  PlayIcon
} from '@heroicons/react/24/outline';

const CampaignManager = () => {
  const [leads, setLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [campaignLoading, setCampaignLoading] = useState(false);
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const [filters, setFilters] = useState({
    states: '',
    counties: '',
    status: 'New'
  });

  useEffect(() => {
    fetchCampaignLeads();
  }, [filters]);

  const fetchCampaignLeads = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getCampaignLeads(filters);
      setLeads(response.data.leads);
    } catch (error) {
      toast.error('Failed to load campaign leads');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(lead => lead._id));
    }
  };

  const handleVoicemailCampaign = async (data) => {
    setCampaignLoading(true);
    try {
      const response = await adminAPI.createVoicemailCampaign({
        ...data,
        leadIds: selectedLeads
      });
      
      toast.success(response.data.message);
      setSelectedLeads([]);
      reset();
    } catch (error) {
      toast.error('Failed to create voicemail campaign');
    } finally {
      setCampaignLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center">
          <MegaphoneIcon className="h-8 w-8 text-primary-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Campaign Manager</h1>
            <p className="mt-1 text-sm text-gray-600">
              Create and manage voicemail campaigns for lead outreach.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Campaign Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">States</label>
            <input
              type="text"
              placeholder="CA,NY,TX (comma separated)"
              value={filters.states}
              onChange={(e) => setFilters({...filters, states: e.target.value})}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Counties</label>
            <input
              type="text"
              placeholder="Los Angeles,Manhattan (comma separated)"
              value={filters.counties}
              onChange={(e) => setFilters({...filters, counties: e.target.value})}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="New">New</option>
              <option value="Contacted">Contacted</option>
              <option value="Interested">Interested</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Lead Selection */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Select Leads ({selectedLeads.length} selected)
              </h3>
              <button
                onClick={handleSelectAll}
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                {selectedLeads.length === leads.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <Loading message="Loading leads..." />
            ) : leads.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No leads found with current filters</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {leads.map((lead) => (
                  <div key={lead._id} className="px-4 py-3 hover:bg-gray-50">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(lead._id)}
                        onChange={() => handleSelectLead(lead._id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{lead.name}</p>
                            <p className="text-sm text-gray-500">{lead.phone}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">{lead.state}</p>
                            <p className="text-sm text-gray-400">{lead.county}</p>
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Campaign Setup */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Voicemail Campaign Setup
            </h3>
          </div>

          <div className="p-6">
            <form onSubmit={handleSubmit(handleVoicemailCampaign)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Agency Name</label>
                <input
                  {...register('agencyName', { required: 'Agency name is required' })}
                  type="text"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Your Agency Name"
                />
                {errors.agencyName && <p className="mt-1 text-sm text-red-600">{errors.agencyName.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Callback Number</label>
                <input
                  {...register('callbackNumber', { required: 'Callback number is required' })}
                  type="tel"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="(555) 123-4567"
                />
                {errors.callbackNumber && <p className="mt-1 text-sm text-red-600">{errors.callbackNumber.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Voicemail Message</label>
                <textarea
                  {...register('message', { required: 'Message is required' })}
                  rows={4}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Hello {{name}}, this is {{agencyName}}. Please call us back at {{callbackNumber}}..."
                />
                {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>}
                <p className="mt-1 text-sm text-gray-500">
                  Use {`{{name}}`}, {`{{agencyName}}`}, and {`{{callbackNumber}}`} as placeholders
                </p>
              </div>

              <button
                type="submit"
                disabled={selectedLeads.length === 0 || campaignLoading}
                className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {campaignLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending Campaign...
                  </div>
                ) : (
                  <>
                    <PhoneIcon className="h-5 w-5 mr-2" />
                    Start Voicemail Campaign ({selectedLeads.length} leads)
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignManager;